import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { UploadCloud, Loader2, FileText, CheckCircle2, Sparkles, AlertTriangle } from "lucide-react";

export default function DataImport() {
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const geo = cfg?.geo || {};
  const metricLabels = useMemo(() => Object.fromEntries((cfg?.metrics || []).map((m) => [m.code, m.name])), [cfg]);
  const territories = draft?.province ? Object.keys(geo[draft.province]?.territories || {}) : [];
  const zones = draft?.province && draft?.territory ? geo[draft.province]?.territories?.[draft.territory] || [] : [];

  const upload = async () => {
    if (!file) { toast.error(t("Choisissez un fichier (.docx, .pdf, .xlsx).")); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft(data);
      toast.success(t("Données extraites · veuillez valider avant enregistrement."));
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setExtracting(false); }
  };

  const confirm = async () => {
    if (!draft.report_date || !draft.province || !draft.health_zone) {
      toast.error(t("Date, province et zone de santé sont requis.")); return;
    }
    setSaving(true);
    try {
      await api.post("/import/confirm", draft);
      toast.success(t("Rapport importé et enregistré dans la base."));
      setDraft(null); setFile(null); if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleAct = (code) => setDraft((d) => ({ ...d, activities: d.activities.includes(code) ? d.activities.filter((x) => x !== code) : [...d.activities, code] }));
  const setMetric = (code, v) => setDraft((d) => ({ ...d, metrics: { ...d.metrics, [code]: Number(v) || 0 } }));

  return (
    <div className="max-w-4xl mx-auto" data-testid="data-import-page">
      <PageHeader title={t("Import des Rapports Historiques")} subtitle={t("Téléversez d'anciens rapports Word/PDF/Excel · extraction assistée par IA avec validation obligatoire")} />

      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 flex gap-3 text-sm text-amber-800">
        <AlertTriangle className="h-5 w-5 shrink-0" />
        <p>{t("Les données extraites par l'IA ne sont jamais enregistrées automatiquement. Vérifiez et corrigez chaque champ ci-dessous avant de confirmer l'ajout à la base.")}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6">
        <Label>Fichier du rapport (.docx, .pdf, .xlsx)</Label>
        <div className="flex flex-col sm:flex-row gap-3 mt-2">
          <Input ref={fileRef} type="file" accept=".docx,.pdf,.xlsx,.xls,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file" />
          <Button onClick={upload} disabled={extracting || !file} className="bg-[#F05A28] hover:bg-[#D84A1B] shrink-0" data-testid="import-extract-btn">
            {extracting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Extraire avec l'IA
          </Button>
        </div>
        {file && <p className="text-xs text-slate-500 mt-2 flex items-center gap-1"><FileText className="h-3 w-3" /> {file.name}</p>}
      </div>

      {draft && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 mt-6" data-testid="import-draft">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-900">Validation des données extraites</h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs">Date du rapport</Label><Input type="date" value={draft.report_date || ""} onChange={(e) => setDraft({ ...draft, report_date: e.target.value })} data-testid="draft-date" /></div>
            <div><Label className="text-xs">Épidémiologiste</Label><Input value={draft.epidemiologist_name || ""} onChange={(e) => setDraft({ ...draft, epidemiologist_name: e.target.value })} /></div>
            <div>
              <Label className="text-xs">Province</Label>
              <Select value={draft.province || ""} onValueChange={(v) => setDraft({ ...draft, province: v, territory: "", health_zone: "" })}>
                <SelectTrigger data-testid="draft-province"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{Object.keys(geo).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Territoire</Label>
              <Select value={draft.territory || ""} onValueChange={(v) => setDraft({ ...draft, territory: v, health_zone: "" })} disabled={!draft.province}>
                <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{territories.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Zone de Santé</Label>
              <Select value={draft.health_zone || ""} onValueChange={(v) => setDraft({ ...draft, health_zone: v })} disabled={!draft.territory}>
                <SelectTrigger data-testid="draft-zone"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5">
            <Label className="text-xs">Activités détectées</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {(cfg?.activities || []).map((a) => (
                <label key={a.code} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={draft.activities?.includes(a.code)} onCheckedChange={() => toggleAct(a.code)} />
                  {a.name}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <Label className="text-xs">Chiffres extraits</Label>
            <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
              {(cfg?.metrics || []).map((m) => (
                <div key={m.code}>
                  <Label className="text-[11px] text-slate-500">{m.name}</Label>
                  <Input type="number" min="0" value={draft.metrics?.[m.code] ?? 0} onChange={(e) => setMetric(m.code, e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          {draft.findings?.length > 0 && (
            <div className="mt-5"><Label className="text-xs">Constats extraits</Label>
              {draft.findings.map((f, i) => <p key={i} className="text-sm text-slate-600">• {f.category}{f.description ? ` · ${f.description}` : ""}</p>)}
            </div>
          )}
          {draft.challenges?.length > 0 && (
            <div className="mt-3"><Label className="text-xs">Défis extraits</Label>
              {draft.challenges.map((c, i) => <p key={i} className="text-sm text-slate-600">• {c.category}{c.description ? ` · ${c.description}` : ""}</p>)}
            </div>
          )}
          {draft.recommendations?.length > 0 && (
            <div className="mt-3"><Label className="text-xs">Recommandations extraites</Label>
              {draft.recommendations.map((r, i) => <p key={i} className="text-sm text-slate-600">• [{r.priority}] {r.recommendation}</p>)}
            </div>
          )}

          <details className="mt-5">
            <summary className="text-xs text-slate-500 cursor-pointer">Voir le texte source extrait</summary>
            <pre className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 mt-2 whitespace-pre-wrap max-h-48 overflow-y-auto">{draft.raw_text}</pre>
          </details>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setDraft(null)}>Annuler</Button>
            <Button onClick={confirm} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-confirm-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Confirmer et enregistrer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
